import React from 'react';
import { Sym } from './m3';

/* ============================================================================
 * Shared admin building blocks: Panel, StatCard, pills, dots, metrics.
 * Ported from the Claude Design prototype (admin-shared.jsx) to TSX.
 * ========================================================================= */

type CSS = React.CSSProperties;

/* ---- Status / member-status / role metadata --------------------------- */
export type DeviceStatus = 'ok' | 'warn' | 'off';
export interface StatusMeta {
  label: string;
  color: string;
  bg: string;
  fg: string;
}
export const STATUS_META: Record<DeviceStatus, StatusMeta> = {
  ok: {
    label: '정상',
    color: 'var(--gs-primary-50)',
    bg: 'var(--md-sys-color-primary-container)',
    fg: 'var(--md-sys-color-on-primary-container)',
  },
  warn: {
    label: '경고',
    color: 'var(--gs-warning)',
    bg: 'var(--gs-warning-container)',
    fg: 'var(--gs-on-warning-container)',
  },
  off: { label: '오프라인', color: '#9AA095', bg: 'var(--md-sys-color-surface-container-high)', fg: 'var(--md-sys-color-on-surface-variant)' },
};

export type MemberStatus = 'active' | 'pending' | 'dormant';
export const MEMBER_STATUS_META: Record<MemberStatus, StatusMeta> = {
  active: {
    label: '활성',
    color: 'var(--gs-primary-50)',
    bg: 'var(--md-sys-color-primary-container)',
    fg: 'var(--md-sys-color-on-primary-container)',
  },
  pending: {
    label: '승인대기',
    color: 'var(--gs-warning)',
    bg: 'var(--gs-warning-container)',
    fg: 'var(--gs-on-warning-container)',
  },
  dormant: {
    label: '휴면',
    color: '#9AA095',
    bg: 'var(--md-sys-color-surface-container-high)',
    fg: 'var(--md-sys-color-on-surface-variant)',
  },
};

/* ---- StatusDot --------------------------------------------------------- */
export function StatusDot({ s }: { s: DeviceStatus }): React.ReactElement {
  return <span className={`gs-dot gs-dot-${s}`} />;
}

/* ---- StatusPill / member status pill ----------------------------------- */
export function StatusPill({
  s,
  meta = STATUS_META as Record<string, StatusMeta>,
}: {
  s: string;
  meta?: Record<string, StatusMeta>;
}): React.ReactElement {
  const m = meta[s] ?? STATUS_META.off;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 24,
        padding: '0 10px',
        borderRadius: 9999,
        background: m.bg,
        color: m.fg,
        font: '600 12px/1 var(--gs-font-kr)',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: 9999, background: m.color }} />
      {m.label}
    </span>
  );
}

/* ---- RolePill ---------------------------------------------------------- */
export function RolePill({ role }: { role: 'manager' | 'user' | string }): React.ReactElement {
  const isManager = role === 'manager';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 22,
        padding: '0 8px',
        borderRadius: 6,
        background: isManager
          ? 'var(--md-sys-color-secondary-container)'
          : 'var(--md-sys-color-surface-container-high)',
        color: isManager
          ? 'var(--md-sys-color-on-secondary-container)'
          : 'var(--md-sys-color-on-surface-variant)',
        font: '500 11px/1 var(--gs-font-kr)',
        whiteSpace: 'nowrap',
      }}
    >
      <Sym name={isManager ? 'shield_person' : 'person'} size={14} />
      {isManager ? '담당자' : '사용자'}
    </span>
  );
}

/* ---- ModelTag (1=환경, 2=환경·생육) ----------------------------------- */
export function ModelTag({ m }: { m: 1 | 2 | number }): React.ReactElement {
  const isGrowth = m === 2;
  return (
    <span className={`gs-tag ${isGrowth ? 'gs-tag-m2' : 'gs-tag-m1'}`}>
      <Sym name={isGrowth ? 'eco' : 'sensors'} size={13} />
      {isGrowth ? '환경 · 생육' : '환경'}
    </span>
  );
}

/* ---- MiniBar ----------------------------------------------------------- */
export function MiniBar({
  value,
  max,
  color = 'var(--md-sys-color-primary)',
  h = 6,
}: {
  value: number;
  max: number;
  color?: string;
  h?: number;
}): React.ReactElement {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div
      style={{
        height: h,
        borderRadius: 9999,
        background: 'var(--md-sys-color-surface-container-high)',
        overflow: 'hidden',
      }}
    >
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 9999 }} />
    </div>
  );
}

/* ---- Metric (mini stat tile) ------------------------------------------- */
export function Metric({
  icon,
  label,
  v,
  u,
  tone = 'var(--md-sys-color-on-surface)',
  warn,
}: {
  icon: string;
  label: string;
  v: React.ReactNode;
  u?: string;
  tone?: string;
  warn?: boolean;
}): React.ReactElement {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 10,
        background: warn ? 'var(--gs-warning-container)' : 'var(--md-sys-color-surface-container-high)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <Sym name={icon} size={15} style={{ color: warn ? 'var(--gs-on-warning-container)' : tone }} />
        <span style={{ font: '500 11px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
          {label}
        </span>
      </div>
      <div className="gs-num" style={{ font: '700 16px/1 var(--gs-font-kr)', color: warn ? 'var(--gs-on-warning-container)' : 'var(--md-sys-color-on-surface)' }}>
        {v}
        {u && <span style={{ font: '500 11px/1 var(--gs-font-kr)', marginLeft: 2, color: 'var(--md-sys-color-on-surface-variant)' }}>{u}</span>}
      </div>
    </div>
  );
}

/* ---- SectionLabel ------------------------------------------------------ */
export function SectionLabel({ children, style }: { children: React.ReactNode; style?: CSS }): React.ReactElement {
  return (
    <div
      style={{
        font: '600 11px/1 var(--gs-font-kr)',
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        color: 'var(--md-sys-color-primary)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ---- InfoRow ----------------------------------------------------------- */
export function InfoRow({
  icon,
  label,
  value,
  labelWidth = 72,
}: {
  icon: string;
  label: string;
  value: React.ReactNode;
  labelWidth?: number;
}): React.ReactElement {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
      <Sym name={icon} size={18} style={{ color: 'var(--md-sys-color-on-surface-variant)' }} />
      <span style={{ width: labelWidth, font: '400 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
        {label}
      </span>
      <span style={{ flex: 1, textAlign: 'right', font: '500 13px/1.4 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
        {value}
      </span>
    </div>
  );
}

/* ---- Panel ------------------------------------------------------------- */
export interface PanelProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
  pad?: boolean;
  style?: CSS;
  bodyStyle?: CSS;
}
export function Panel({
  title,
  subtitle,
  action,
  children,
  pad = true,
  style,
  bodyStyle,
}: PanelProps): React.ReactElement {
  const hasHead = title != null || action != null;
  return (
    <section
      style={{
        background: 'var(--md-sys-color-surface-container-lowest)',
        border: '1px solid var(--md-sys-color-outline-variant)',
        borderRadius: 18,
        overflow: 'hidden',
        ...style,
      }}
    >
      {hasHead && (
        <div
          style={{
            padding: '16px 20px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderBottom: children != null ? '1px solid var(--md-sys-color-outline-variant)' : 'none',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {title != null && (
              <div style={{ font: '700 15px/20px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                {title}
              </div>
            )}
            {subtitle != null && (
              <div style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginTop: 2 }}>
                {subtitle}
              </div>
            )}
          </div>
          {action}
        </div>
      )}
      <div style={{ padding: pad ? 20 : 0, ...bodyStyle }}>{children}</div>
    </section>
  );
}

/* ---- StatCard ---------------------------------------------------------- */
export type StatTone = 'primary' | 'secondary' | 'warn' | 'error' | 'muted';
const TONE_MAP: Record<StatTone, { bg: string; fg: string }> = {
  primary: { bg: 'var(--md-sys-color-primary-container)', fg: 'var(--md-sys-color-on-primary-container)' },
  secondary: { bg: 'var(--md-sys-color-secondary-container)', fg: 'var(--md-sys-color-on-secondary-container)' },
  warn: { bg: 'var(--gs-warning-container)', fg: 'var(--gs-on-warning-container)' },
  error: { bg: 'var(--md-sys-color-error-container)', fg: 'var(--md-sys-color-on-error-container)' },
  muted: { bg: 'var(--md-sys-color-surface-container-high)', fg: 'var(--md-sys-color-on-surface-variant)' },
};
export interface StatCardProps {
  icon: string;
  label: string;
  value: React.ReactNode;
  unit?: string;
  tone?: StatTone;
  delta?: { dir: 'up' | 'down' | 'flat'; val: React.ReactNode };
  sub?: React.ReactNode;
  style?: CSS;
}
export function StatCard({
  icon,
  label,
  value,
  unit,
  tone = 'primary',
  delta,
  sub,
  style,
}: StatCardProps): React.ReactElement {
  const t = TONE_MAP[tone];
  const deltaColor =
    delta?.dir === 'up'
      ? 'var(--gs-primary-50)'
      : delta?.dir === 'down'
        ? 'var(--gs-error)'
        : 'var(--md-sys-color-on-surface-variant)';
  return (
    <div
      style={{
        background: 'var(--md-sys-color-surface-container-lowest)',
        border: '1px solid var(--md-sys-color-outline-variant)',
        borderRadius: 18,
        padding: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ font: '500 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
          {label}
        </span>
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 11,
            background: t.bg,
            color: t.fg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Sym name={icon} size={20} fill={1} />
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        <span className="gs-num" style={{ font: '700 30px/30px var(--gs-font-kr)', letterSpacing: -1, color: 'var(--md-sys-color-on-surface)' }}>
          {value}
          {unit && <span style={{ font: '500 14px/1 var(--gs-font-kr)', marginLeft: 3, letterSpacing: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>{unit}</span>}
        </span>
        {delta && (
          <span style={{ display: 'inline-flex', alignItems: 'center', font: '600 12px/1 var(--gs-font-kr)', color: deltaColor, marginBottom: 3 }}>
            <Sym name={delta.dir === 'up' ? 'arrow_drop_up' : delta.dir === 'down' ? 'arrow_drop_down' : 'remove'} size={18} />
            {delta.val}
          </span>
        )}
      </div>
      {sub != null && (
        <div style={{ font: '400 12px/1.4 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{sub}</div>
      )}
    </div>
  );
}
